import { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Loader2 } from "lucide-react";
import { useToast } from "@/project/karuna/hooks/use-toast"; // @ts-ignore - use-toast is not a valid module
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";
import { createForm } from "@formily/core";
import SchemaFormAutoSubmit from "@/components/Schema/SchemaFormAutoSubmit";
import { getUserInfo } from "@/lib/storeManager";
import { DynamicModuleService, SchemaService } from "@/services/APIs";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/project/karuna/contexts/AuthContext"; // @ts-ignore - AuthContext is not a valid module
import { supabase } from "@/project/karuna/integrations/supabase/client"; // @ts-ignore - supabase is not a valid module
import { removeMetaFields } from "@/lib/removeMetaFields";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/project/karuna/components/ui/dialog"; // @ts-ignore

const SCHEMA_NAME = "Activity Submission";
const SCHOOL_PROFILE_SCHEMA_NAME = "School Profile";
const ACTIVITY_SCHEMA_NAME = "Activity Management";

export type SchoolEventSubmissionFormProps = {
  eventId: string | undefined;
  /** Called after successful submit (toast already shown). If omitted, navigates to submissions. */
  onSuccess?: () => void;
  /** Cancel / close without submitting */
  onCancel?: () => void;
  /** Show the standalone page heading (hide when embedded in a dialog) */
  showPageHeader?: boolean;
};

export const SchoolEventSubmissionForm = ({
  eventId,
  onSuccess,
  onCancel,
  showPageHeader = true,
}: SchoolEventSubmissionFormProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const getLink = useKarunaLink();

  const [loading, setLoading] = useState(false);
  const formRef = useRef<{ handleSave: () => Promise<void> }>(null);

  const form = useMemo(() => {
    void eventId;
    return createForm({});
  }, [eventId]);

  const normalizeMobileNumber = (mobileNumber: string): string => {
    return String(mobileNumber ?? "")
      .replace(/\D/g, "")
      .slice(0, 10);
  };

  const userInfo = getUserInfo();
  const normalizedContact = userInfo?.phone_number
    ? normalizeMobileNumber(userInfo.phone_number)
    : "";

  const {
    data: schemaData,
    isLoading: schemaLoading,
    isError: schemaError,
  } = useQuery({
    queryKey: ["schema", SCHEMA_NAME],
    queryFn: () => SchemaService.getSchemaByName(SCHEMA_NAME),
    enabled: !!SCHEMA_NAME,
    retry: false,
  });

  const { data: schoolProfileData } = useQuery({
    queryKey: ["school-profile", SCHOOL_PROFILE_SCHEMA_NAME, normalizedContact],
    queryFn: async () => {
      if (!normalizedContact) {
        return null;
      }
      try {
        const allProfiles = await DynamicModuleService.getGridColItemsAllPages(
          SCHOOL_PROFILE_SCHEMA_NAME
        );

        const profile = allProfiles.find((record: any) => {
          const recordContact = normalizeMobileNumber(
            record.contact ??
              record.school_contact ??
              record.schoolcontact ??
              ""
          );
          return recordContact === normalizedContact;
        });

        if (!profile) {
          return null;
        }

        const profileId = profile.id ?? profile._id;
        if (profileId) {
          try {
            const fullData = await DynamicModuleService.getItemById(
              SCHOOL_PROFILE_SCHEMA_NAME,
              profileId
            );
            const fullRecord = fullData?.data ?? fullData ?? profile;
            return { ...profile, ...fullRecord };
          } catch (_err) {
            return profile;
          }
        }
        return profile;
      } catch (error) {
        console.error("Error fetching school profile:", error);
        return null;
      }
    },
    enabled: !!normalizedContact,
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (schoolProfileData && schemaData) {
      const profileMapping: Record<string, string> = {
        kcno: "kcno",
        schoolname: "schoolname",
        kendra: "nearestkendra",
        principal: "principalname",
        contact: "schoolcontact",
        schoolemail: "schoolemail",
        teachername: "teachername",
        teachercontact: "teachermobile",
        teacheremail: "teacheremail",
      };

      const prefillValues: Record<string, any> = {};

      if (schoolProfileData.kcno) {
        prefillValues["basicInfo.kcno"] = schoolProfileData.kcno;
      }
      if (schoolProfileData.schoolname) {
        prefillValues["basicInfo.schoolname"] = schoolProfileData.schoolname;
      }
      if (schoolProfileData.kendra) {
        prefillValues["basicInfo.nearestkendra"] = schoolProfileData.kendra;
      }
      const principalValue =
        schoolProfileData.principal ??
        schoolProfileData.principalname ??
        schoolProfileData.principal_name;
      if (principalValue) {
        prefillValues["basicInfo.principalname"] = principalValue;
      }
      if (schoolProfileData.contact) {
        prefillValues["basicInfo.schoolcontact"] = schoolProfileData.contact;
      }
      if (schoolProfileData.schoolemail) {
        prefillValues["basicInfo.schoolemail"] = schoolProfileData.schoolemail;
      }

      if (schoolProfileData.teachername) {
        prefillValues["teacherInfo.teachername"] =
          schoolProfileData.teachername;
      }
      if (schoolProfileData.teachercontact) {
        prefillValues["teacherInfo.teachermobile"] =
          schoolProfileData.teachercontact;
      }
      if (schoolProfileData.teacheremail) {
        prefillValues["teacherInfo.teacheremail"] =
          schoolProfileData.teacheremail;
      }

      Object.entries(profileMapping).forEach(([profileKey, formKey]) => {
        if (schoolProfileData[profileKey]) {
          prefillValues[formKey] = schoolProfileData[profileKey];
        }
      });

      if (Object.keys(prefillValues).length > 0) {
        form.setInitialValues(prefillValues);
        form.setValues(prefillValues);

        const fieldsToDisable = [
          "kcno",
          "schoolname",
          "nearestkendra",
          "principalname",
          "schoolcontact",
          "schoolemail",
          "teachername",
          "teachermobile",
          "teacheremail",
        ];

        setTimeout(() => {
          fieldsToDisable.forEach(fieldName => {
            const paths = [
              `basicInfo.${fieldName}`,
              `teacherInfo.${fieldName}`,
              fieldName,
            ];

            paths.forEach(path => {
              form.setFieldState(path, state => {
                state.disabled = true;
                state.componentProps = {
                  ...state.componentProps,
                  disabled: true,
                };
              });
            });
          });
        }, 100);
      }
    }
  }, [schoolProfileData, schemaData, form]);

  useEffect(() => {
    if (schemaData) {
      const adminOnlyFields = [
        "status",
        "score",
        "comments",
        "activitytitle",
        "tvChannel.status",
        "tvChannel.score",
        "tvChannel.comments",
        "tvChannel.activitytitle",
      ];

      setTimeout(() => {
        adminOnlyFields.forEach(fieldName => {
          form.setFieldState(fieldName, state => {
            state.visible = false;
            state.display = "none";
            state.componentProps = {
              ...state.componentProps,
              style: { display: "none" },
            };
          });
        });
      }, 200);
    }
  }, [schemaData, form]);

  const handleSubmit = async () => {
    if (!formRef.current) {
      return;
    }

    try {
      const userInfoSubmit = getUserInfo();
      if (!userInfoSubmit?.phone_number) {
        toast({
          title: "Authentication Error",
          description: "Unable to identify school. Please login again.",
          variant: "destructive",
        });
        return;
      }

      await form.validate();

      const normalizedContactSubmit = normalizeMobileNumber(
        userInfoSubmit.phone_number
      );

      let activityTitle = "";
      if (eventId) {
        try {
          const activityData = await DynamicModuleService.getItemById(
            ACTIVITY_SCHEMA_NAME,
            eventId
          );
          const activity = activityData?.data ?? activityData;
          activityTitle =
            activity?.title ??
            activity?.activitytitle ??
            activity?.activity_title ??
            "";
        } catch (error) {
          console.warn("Could not fetch activity title:", error);
        }
      }

      if (user?.id) {
        try {
          const { error: schoolError } = await supabase
            .from("schools")
            .select("id")
            .eq("user_id", user.id)
            .maybeSingle();

          if (schoolError) {
            console.error("Error fetching school_id:", schoolError);
          }
        } catch (error) {
          console.error("Exception fetching school_id:", error);
        }
      }

      const currentValues = removeMetaFields(form.values);

      const adminOnlyFields = ["status", "score", "comments", "activitytitle"];
      const cleanedCurrentValues = { ...currentValues };
      adminOnlyFields.forEach(field => {
        delete cleanedCurrentValues[field];
        delete cleanedCurrentValues[`tvChannel.${field}`];
      });

      const submissionValues: Record<string, any> = {
        ...cleanedCurrentValues,
        schoolcontact: normalizedContactSubmit,
        "basicInfo.schoolcontact": normalizedContactSubmit,
        contact: normalizedContactSubmit,
        school_contact: normalizedContactSubmit,
        event_id: eventId,
        eventid: eventId,
        activitytitle: activityTitle,
      };

      form.setValues(submissionValues);

      setLoading(true);

      const cleanedValues = Object.fromEntries(
        Object.entries(submissionValues).filter(([_, value]) => {
          if (value === undefined || value === null || value === "") {
            return false;
          }
          if (
            typeof value === "object" &&
            !Array.isArray(value) &&
            Object.keys(value).length === 0
          ) {
            return false;
          }
          if (Array.isArray(value) && value.length === 0) {
            return false;
          }
          return true;
        })
      );

      const hasContact =
        cleanedValues.schoolcontact ??
        cleanedValues.contact ??
        cleanedValues.school_contact;
      const hasEventId = cleanedValues.event_id ?? cleanedValues.eventid;

      if (!hasContact) {
        toast({
          title: "Warning",
          description:
            "Contact number not found. Please ensure your profile is complete.",
          variant: "destructive",
        });
      }

      if (!hasEventId) {
        console.error(
          "No event_id found; submission may not link to activity."
        );
      }

      const response = await DynamicModuleService.createItem(
        SCHEMA_NAME,
        cleanedValues
      );
      console.log("Submission saved, response:", response);

      toast({
        title: "Report Submitted Successfully!",
        description: "Your activity report has been submitted for review.",
      });

      window.dispatchEvent(new Event("submission-created"));

      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        } else {
          navigate(getLink("/school/submissions"));
        }
      }, 1500);
    } catch (error: any) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description: error.message ?? "Please fill out all the details.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate(getLink(`/school/activities/${eventId ?? ""}`));
    }
  };

  if (schemaLoading) {
    return (
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading form...</span>
        </div>
      </div>
    );
  }

  if (schemaError || (!schemaLoading && !schemaData)) {
    return (
      <div className="mx-auto max-w-6xl">
        {showPageHeader ? (
          <div className="mb-8">
            <h1 className="mb-2 text-3xl font-bold">Submit Activity Report</h1>
          </div>
        ) : null}
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <h3 className="mb-2 font-semibold text-red-900">Schema Not Found</h3>
          <p className="text-sm text-red-700">
            The &quot;{SCHEMA_NAME}&quot; schema could not be loaded. Please
            ensure the schema exists in the system.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl">
      {showPageHeader ? (
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold">Submit Activity Report</h1>
          <p className="text-muted-foreground">
            Fill out the form below to submit your Karuna Club activity report
          </p>
        </div>
      ) : null}

      <div className="space-y-6">
        <SchemaFormAutoSubmit
          ref={formRef}
          form={form}
          schemaName={SCHEMA_NAME}
        />

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            className="gap-2 border-0 bg-gradient-hero disabled:cursor-not-allowed disabled:opacity-50"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Report"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export type SchoolEventSubmissionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string | undefined;
};

export const SchoolEventSubmissionDialog = ({
  open,
  onOpenChange,
  eventId,
}: SchoolEventSubmissionDialogProps) => {
  const navigate = useNavigate();
  const getLink = useKarunaLink();

  const handleSuccess = () => {
    onOpenChange(false);
    navigate(getLink("/school/submissions"));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[min(90vh,900px)] max-w-4xl gap-0 overflow-y-auto p-6 sm:max-w-4xl">
        <DialogHeader className="pb-4 text-left">
          <DialogTitle>Submit Activity Report</DialogTitle>
          <DialogDescription>
            Fill out the form below to submit your Karuna Club activity report.
          </DialogDescription>
        </DialogHeader>
        {eventId ? (
          <SchoolEventSubmissionForm
            key={eventId}
            eventId={eventId}
            showPageHeader={false}
            onCancel={() => onOpenChange(false)}
            onSuccess={handleSuccess}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
